/** @typedef {{ requireEmail: boolean, requireWhatsapp: boolean, orMode: boolean }} CustomerContactRequirements */

/** @param {unknown} v */
export function tenantFlagFromValue(v) {
  if (v === true) return true;
  if (v === false || v == null) return false;
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'on';
  }
  return false;
}

/**
 * @param {Record<string, unknown> | null | undefined} tenant
 * @returns {CustomerContactRequirements}
 */
export function getCustomerContactRequirements(tenant) {
  let requireEmail = tenantFlagFromValue(tenant?.require_customer_email ?? 1);
  let requireWhatsapp = tenantFlagFromValue(tenant?.require_customer_whatsapp ?? 0);
  if (!requireEmail && !requireWhatsapp) {
    requireEmail = true;
  }
  return {
    requireEmail,
    requireWhatsapp,
    orMode: requireEmail && requireWhatsapp,
  };
}

/** @param {unknown} email */
export function customerContactHasValidEmail(email) {
  const e = String(email || '').trim().toLowerCase();
  return e !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

/** @param {unknown} phone */
export function customerContactHasValidPhone(phone) {
  return String(phone || '').replace(/\D/g, '').length >= 8;
}

/**
 * @param {unknown} email
 * @param {unknown} phone
 * @param {CustomerContactRequirements} req
 * @returns {string | null}
 */
export function validateCustomerContact(email, phone, req) {
  const hasEmail = customerContactHasValidEmail(email);
  const emailRaw = String(email || '').trim();
  const emailInvalid = emailRaw !== '' && !hasEmail;

  if (emailInvalid) {
    return 'E-mail inválido ou incompleto.';
  }

  const hasPhone = customerContactHasValidPhone(phone);

  if (req.orMode) {
    if (!hasEmail && !hasPhone) {
      return 'Informe e-mail ou WhatsApp (pelo menos um).';
    }
    return null;
  }

  if (req.requireEmail && !hasEmail) {
    return 'E-mail é obrigatório.';
  }

  if (req.requireWhatsapp && !hasPhone) {
    return 'WhatsApp é obrigatório.';
  }

  return null;
}

/** @param {CustomerContactRequirements} req */
export function validateLoginIdentity(value, req) {
  const raw = String(value || '').trim();
  if (!raw) {
    if (req.orMode) return 'Informe e-mail ou WhatsApp.';
    if (req.requireWhatsapp && !req.requireEmail) return 'WhatsApp é obrigatório.';
    return 'E-mail é obrigatório.';
  }

  const hasEmail = customerContactHasValidEmail(raw);
  const hasPhone = customerContactHasValidPhone(raw);

  if (req.orMode) {
    if (!hasEmail && !hasPhone) {
      return 'Informe e-mail ou WhatsApp válido.';
    }
    return null;
  }

  if (req.requireWhatsapp && !req.requireEmail) {
    if (!hasPhone) return 'WhatsApp é obrigatório.';
    return null;
  }

  if (!hasEmail) return 'E-mail é obrigatório.';
  return null;
}

/**
 * @param {CustomerContactRequirements} req
 * @param {'email' | 'phone'} field
 */
export function contactFieldHtmlRequired(req, field) {
  if (req.orMode) return false;
  if (field === 'email') return req.requireEmail;
  return req.requireWhatsapp;
}

/** @param {CustomerContactRequirements} req */
export function contactRequirementsHint(req) {
  if (req.orMode) {
    return 'Informe e-mail ou WhatsApp — basta um dos dois.';
  }
  if (req.requireEmail && !req.requireWhatsapp) {
    return 'E-mail obrigatório.';
  }
  if (req.requireWhatsapp && !req.requireEmail) {
    return 'WhatsApp obrigatório.';
  }
  return '';
}

/** @param {CustomerContactRequirements} req */
export function loginIdentityPlaceholder(req) {
  if (req.orMode) return 'E-mail ou WhatsApp';
  if (req.requireWhatsapp && !req.requireEmail) return 'WhatsApp';
  return 'E-mail';
}
